import React, { useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress, InputAdornment } from "@mui/material";
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { styled } from "@mui/system";

import { getQuestions, saveAnswer, logoutClear } from "../../redux/actions/appActions";
import { useDispatch, useSelector } from "react-redux";
import GreatGoing from "./GreatGoing";
import WellDone from "./WellDone";
import SectionFinal from "./SectionFinal";
import { useNavigate } from "react-router-dom";
import { ROUTE_COMPLETE_PROFILE, ROUTE_MAIN } from "../../constants/routes";

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  paddingTop: "16px",
  paddingBottom: "16px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
  marginLeft: "10px",
});

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "10px 20px",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Question = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const Logout = () => {
    dispatch(logoutClear(() => {
      navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  const questions = useSelector((state: any) => state.app.questions);
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);

  const [current, setCurrent] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, Record<number, any>>>({});

  const handleOptionClick = (optionId: any) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [currentSection]: {
        ...(prev[currentSection] || {}),
        [current]: optionId,
      },
    }));
  };

  useEffect(() => {
    dispatch(getQuestions());
  }, [dispatch]);

  const handleNext = () => {
    const currentSectionQuestions = questions?.[currentSection]?.questions || [];
    const currentQuestion = currentSectionQuestions?.[current];
    const userAnswer = currentQuestion?.userAnswer; // Pre-existing answer from backend
    const currentOption = selectedOptions?.[currentSection]?.[current] || userAnswer;

    // Ensure the question exists
    if (!currentQuestion) {
      console.error("Current question does not exist.");
      return;
    }

    // Ensure an answer exists (either from selectedOptions or backend)
    if (!currentOption) {
      console.error("No option selected for the current question.");
      return;
    }

    const payload = {
      questionId: currentQuestion?.questionId,
      optionId: currentOption,
    };

    // Save the answer and move to the next question
    dispatch(saveAnswer(payload));
    setCurrent((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrent(current - 1);
  };

  useEffect(() => {
    if (questions?.[0]?.questions.length - current === 15) {
      setOpen(true);
    }
  }, [current]);

  const isSelected =
    selectedOptions?.[currentSection]?.[current] || questions?.[currentSection]?.questions?.[current]?.userAnswer;

  if (currentSection === 0 && questions?.[0]?.questions.length - current === 0) {
    return (
      <WellDone
        handleClose={() => {
          setCurrentSection(currentSection + 1);
          setCurrent(0);
        }}
      />
    );
  } else if (
    questions?.length === currentSection + 1 &&
    questions?.[currentSection]?.questions.length - current === 0
  ) {
    return <SectionFinal />;
  }
  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <GreatGoing currentSection={currentSection} open={open} handleClose={handleClose} />
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          height: "60px",
          paddingX: 2,
        }}
      >
        <Header
          onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}
          sx={{
            position: "absolute",
            left: -5,
          }}
        >
          <Logo src="/images/client-website/seevi-logo.png" alt="Seevi Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1, position: "absolute", right: 10 }}>
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <HomeIcon />
            </InputAdornment>
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <LogoutIcon />
            </InputAdornment>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "rgb(255, 150, 96)",
            padding: "8px 16px",
            borderRadius: "8px",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            Section {currentSection + 1} of {questions?.length}
          </Typography>
        </Box>
      </Box>

      {ajaxCallsInProgress ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
          <Box display="flex" alignItems="center" sx={{ backgroundColor: "#eeeeee" }} pl={1} pr={5} mt={1}>
            <StyledTextBox px={2} sx={{ backgroundColor: "#ffffff" }}>
              <Typography
                sx={{ color: "#4242AD", fontWeight: "600", fontSize: "25px" }}
                className="custom-typography-video"
              >
                Question {current + 1} of {questions?.[currentSection]?.questions?.length}
              </Typography>
            </StyledTextBox>
          </Box>

          <Box display="flex" alignItems="center" sx={{ backgroundColor: "#eeeeee" }} pl={1} pr={5} mt={1}>
            <StyledTextBox px={2} sx={{ backgroundColor: "#ffffff" }}>
              <Typography className="custom-typography-video">
                {questions?.[currentSection]?.questions?.[current]?.questionText}
              </Typography>
            </StyledTextBox>
          </Box>

          <Box display="flex" alignItems="center" sx={{ backgroundColor: "#eeeeee" }} pl={3} pr={5} mt={3}>
            <Typography
              sx={{ color: "#9B9B9B", fontWeight: "600", fontSize: "25px" }}
              className="custom-typography-video"
            >
              Answer Options
            </Typography>
          </Box>

          {questions?.[currentSection]?.questions?.[current]?.options?.map((el: any, index: number) => {
            const userAnswer = questions?.[currentSection]?.questions?.[current]?.userAnswer;
            const currentSelection = selectedOptions?.[currentSection]?.[current];
            const optionSelected = currentSelection ? currentSelection === el?.optionId : userAnswer === el?.optionId;

            return (
              <Box
                key={el?.optionId}
                display="flex"
                alignItems="center"
                sx={{ backgroundColor: "#eeeeee" }}
                pl={1}
                pr={5}
                mt={1}
                onClick={() => handleOptionClick(el?.optionId)}
              >
                <StyledTextBox
                  px={2}
                  sx={{
                    backgroundColor: "#E6E4FE",
                    border: optionSelected ? "2px solid #756DE9" : "2px dotted #756DE9",
                  }}
                >
                  <Typography
                    className="custom-typography-video"
                    variant="body1"
                    component="span"
                    sx={{ minWidth: "20px" }}
                  >
                    {String.fromCharCode(65 + index)}.
                  </Typography>
                  <Typography className="custom-typography-video" variant="body1" component="span" sx={{ ml: 1 }}>
                    {questions?.[currentSection]?.questions?.[current]?.options?.[index]?.optionText}
                  </Typography>
                </StyledTextBox>
              </Box>
            );
          })}

          <Box
            display="flex"
            flexDirection="column"
            p={2}
            bgcolor="#ffffff"
            sx={{ borderRadius: "0px", marginTop: "80px" }}
          >
            <Box display="flex">
              <Button
                sx={{
                  backgroundColor: "white",
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "25%",
                  color: "#000000",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                variant="contained"
                size="small"
                style={{ padding: "15px 40px" }}
                onClick={handleBack}
                disabled={current === 0} // Disable Back button on first question
              >
                Back
              </Button>
              <Box flexGrow={1}></Box>
              <Button
                disabled={!isSelected}
                sx={{
                  borderRadius: "99px",
                  fontSize: "16px",
                  textTransform: "capitalize",
                  width: "70%",
                  paddingY: "15px",
                  paddingX: 0,
                  backgroundColor: "#756DE9",
                }}
                variant="contained"
                size="small"
                onClick={handleNext}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Question;
